import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDrag, useDrop } from 'react-dnd';

// Components
import { MaterialIcon } from '../../../../../../../../components/MaterialIcon';
import { PredicateGroupWrapper } from './PredicateGroupWrapper';
import { PredicatePart } from './PredicatePart';
import { PredicateConjunction } from './PredicateConjunction';
import { PredicateRow, FlexContainer } from './PredicateRow';
import { ActionsContainer } from './PredicateRowActions';
import { PredicateGroupFunctionOperator } from './PredicateGroupFunctionOperator';

// Context
import { RuleEditorContext } from '../context';

// Enums
import { RULE_EDITOR_CHARS } from '../enums/ruleEditorChars';
import { dtoState } from '../../../../../../../../common/enums/dtoState';

// Hooks
import { useCriteriaStorePredicate } from '../../../../../../state/criteria/hooks/useCriteriaStorePredicate';
import { useCriteriaStoreLastMovedUuid } from '../../../../../../state/criteria/hooks/useCriteriaStoreLastMovedUuid';
import { useCriteriaStoreAcceptDropPredicateUuids } from '../../../../../../state/criteria/hooks/useCriteriaStoreAcceptDropPredicateUuids';
import { useCriteriaStoreMovePredicateToGroup } from '../../../../../../state/criteria/hooks/useCriteriaStoreMovePredicateToGroup';

// Styles
import { PredicateRowIndent, calculateIndent } from '../../../../../../../../common/styles/mixins';

const RowHighlight = keyframes`
    0% {
        background: #aeffd245;
    }
    25% {
        background: #aeffd245;
    }
    100% {
        background: none;
    }
`;

const Container = styled.ul`
    list-style: none;
    padding-left: 0;
    position: relative;
    ${({ isgroup, islast }) =>
        isgroup &&
        css`
            :after {
                content: '';
                border-left: 3px dotted #c8eae4;
                top: 32px;
                position: absolute;
                left: ${({ indent }) => calculateIndent(indent)};
                bottom: ${({ islast }) => (islast ? '35px' : '74px')};
                border-radius: 2px;
            }
        `}
    ${({ isdragging }) =>
        isdragging === 'true' &&
        css`
            background: rgba(127, 207, 250, 0.3);
            color: rgba(127, 207, 250, 0.3) !important;
        `}
`;
const HighlightableContainer = styled.div`
    :before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: -300px;
        left: 0px;
        right: -14px;
        background: transparent;
    }
    ${({ moved }) =>
        moved &&
        css`
            :before {
                animation: ${RowHighlight} 2s linear;
            }
        `}
`;

const CollapseContainer = styled.div`
    display: flex;
    flex: 1;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
`;

const IndentWrapper = styled(FlexContainer)`
    ${PredicateRowIndent}
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ClosingGroupFnRow = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    z-index: 1;
`;

const CollapseButton = styled(({ collapsed, ...rest }) => <button {...rest} />)`
    background: none;
    color: inherit;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    outline: inherit;
    line-height: 0;
    i {
        transform: ${({ collapsed }) => `rotate(${collapsed ? '0' : '90'}deg)`};
        transition: transform 0.3s ease;
    }
`;

export const DraggablePredicateElement = ({ children, ...props }) => {
    const predicate = useCriteriaStorePredicate(props.predicateUuid);
    const isGroup = React.useMemo(() => {
        return predicate?.predicateType === 'PredicateGroup';
    }, [predicate]);
    const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
        id: props.predicateUuid,
        animateLayoutChanges: () => false
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition
    };

    return (
        <Container
            ref={setNodeRef}
            style={style}
            isdragging={isDragging?.toString() ?? 'false'}
            indent={props.nestedLevel}
            isgroup={isGroup}
            islast={props.isLast}
        >
            <BasePredicateElement {...props} dragHandleProps={{ attributes, listeners, isDragging }}>
                {children}
            </BasePredicateElement>
        </Container>
    );
};

//   props: { childUuids: predicate?.predicates ?? [], dropGroupUuid: predicate.uuid, isLastMoved }

export const DroppableContainer = React.memo(function DroppableContainer({ children, predicateUuid, isLastMoved }) {
    const { setEdited } = React.useContext(RuleEditorContext);
    const movePredicateToGroup = useCriteriaStoreMovePredicateToGroup();
    const acceptedGroupUuids = useCriteriaStoreAcceptDropPredicateUuids(predicateUuid);
    const handleDrop = React.useCallback(
        (droppedUuid, droppedGroupUuid) => {
            setEdited(true);
            movePredicateToGroup({
                predicateUuid: droppedUuid,
                predicateGroupUuid: droppedGroupUuid
            });
            document.body.focus();
        },
        [setEdited, movePredicateToGroup]
    );
    const [{ isOver }, drop] = useDrop(
        () => ({
            accept: acceptedGroupUuids,
            drop(_item, monitor) {
                if (monitor.didDrop()) return;
                handleDrop(monitor.getItemType(), predicateUuid);

                return undefined;
            },
            collect: (monitor) => ({
                isOver: monitor.isOver({ shallow: true }),
                canDrop: monitor.canDrop(),
                draggingColor: monitor.getItemType()
            })
        }),
        [handleDrop, acceptedGroupUuids, predicateUuid]
    );
    const background = isOver ? '#e7f6ff' : 'transparent';

    return (
        <HighlightableContainer ref={drop} role="TargetBox" style={{ background }} moved={isLastMoved}>
            {children}
        </HighlightableContainer>
    );
});

DroppableContainer.propTypes = {
    children: PropTypes.node,
    // childUuids: PropTypes.arrayOf(PropTypes.string),
    // dropGroupUuid: PropTypes.string,
    predicateUuid: PropTypes.string,
    isLastMoved: PropTypes.bool
};

export const NonDroppableContainer = ({ children, isLastMoved }) => {
    return <HighlightableContainer moved={isLastMoved}>{children}</HighlightableContainer>;
};

NonDroppableContainer.propTypes = {
    children: PropTypes.node,
    isLastMoved: PropTypes.bool
};

export const BasePredicateElement = ({
    previewOnly,
    collapsed,
    onCollapse,
    predicateUuid,
    isLast,
    canReorder,
    nestedLevel,
    dragHandleProps = {},
    children
}) => {
    const predicate = useCriteriaStorePredicate(predicateUuid);
    const lastMovedUuid = useCriteriaStoreLastMovedUuid();
    const isLastMoved = React.useMemo(() => {
        return predicateUuid === lastMovedUuid;
    }, [predicateUuid, lastMovedUuid]);
    const isGroup = React.useMemo(() => {
        return predicate?.predicateType === 'PredicateGroup';
    }, [predicate]);
    const hasGroupFunction = React.useMemo(() => {
        return isGroup && predicate.groupFunctionDTO !== undefined;
    }, [predicate]);

    const Wrapper = React.useMemo(
        () =>
            isGroup
                ? {
                      MainWrapper: {
                          Component: DroppableContainer,
                          props: { predicateUuid, isLastMoved }
                      },
                      PartsWrapper: {
                          Component: PredicateGroupWrapper,
                          props: { predicate, hasGroupFunction }
                      }
                  }
                : {
                      MainWrapper: {
                          Component: NonDroppableContainer,
                          props: { isLastMoved }
                      },
                      PartsWrapper: {
                          Component: React.Fragment,
                          props: {}
                      }
                  },
        [isGroup, predicate, hasGroupFunction, isLastMoved]
    );
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: predicate?.uuid,
            collect: (monitor) => ({
                isDragging: monitor.isDragging()
            })
        }),
        [predicate]
    );

    if (predicate?.dtoState === dtoState.Deleted) return null;
    return (
        <>
            <Wrapper.MainWrapper.Component {...Wrapper.MainWrapper.props}>
                <div ref={drag} role="SourceBox">
                    {predicate ? (
                        <>
                            <PredicateRow
                                previewOnly={previewOnly}
                                predicateUuid={predicateUuid}
                                isGroup={isGroup}
                                dragHandleProps={{
                                    attributes: dragHandleProps.attributes ?? {},
                                    listeners: dragHandleProps.listeners ?? {},
                                    isDragging: dragHandleProps.isDragging ?? false
                                }}
                                canReorder={canReorder}
                                nestedLevel={nestedLevel}
                            >
                                <CollapseContainer
                                    tabIndex="0"
                                    role="button"
                                    aria-pressed="false"
                                    onClick={() => {
                                        if (isGroup) onCollapse();
                                    }}
                                    onKeyDown={() => {
                                        if (isGroup) onCollapse();
                                    }}
                                >
                                    <Wrapper.PartsWrapper.Component {...Wrapper.PartsWrapper.props}>
                                        {predicate.predicateParts.map((partUuid, index) => (
                                            <PredicatePart
                                                predicatePartUuid={partUuid}
                                                predicateType={predicate.predicateType}
                                                key={index}
                                            />
                                        ))}
                                    </Wrapper.PartsWrapper.Component>
                                    {isGroup ? (
                                        <>
                                            <span style={{ position: 'relative', zIndex: 1 }}>
                                                {RULE_EDITOR_CHARS.GROUP_OPEN}
                                            </span>
                                            <CollapseButton collapsed={collapsed} aria-modal>
                                                <MaterialIcon icon={'arrow_right'} />
                                            </CollapseButton>
                                        </>
                                    ) : null}
                                </CollapseContainer>
                            </PredicateRow>
                        </>
                    ) : null}
                </div>
                {children}
                {isGroup && !collapsed ? (
                    <IndentWrapper indent={nestedLevel}>
                        <ClosingGroupFnRow>
                            <span>
                                {RULE_EDITOR_CHARS.GROUP_CLOSE}
                                {hasGroupFunction ? RULE_EDITOR_CHARS.FUNC_CLOSE : ''}
                            </span>
                            {predicate.groupFunctionOperatorDTO && (
                                <PredicateGroupFunctionOperator predicateUuid={predicate.uuid} />
                            )}
                        </ClosingGroupFnRow>
                        <ActionsContainer />
                    </IndentWrapper>
                ) : null}
            </Wrapper.MainWrapper.Component>
            {predicate.predicateConjunction && !isLast ? (
                <PredicateConjunction nestedLevel={nestedLevel}>{predicate.predicateConjunction}</PredicateConjunction>
            ) : null}
        </>
    );
};

export const PredicateElement = ({ previewOnly, ...props }) => {
    return previewOnly ? (
        <BasePredicateElement previewOnly={previewOnly} {...props} />
    ) : (
        <DraggablePredicateElement {...props} />
    );
};

export default PredicateElement;

const commonPropTypes = {
    collapsed: PropTypes.bool,
    isLast: PropTypes.bool,
    canReorder: PropTypes.bool,
    onCollapse: PropTypes.func,
    predicateUuid: PropTypes.string,
    children: PropTypes.node,
    nestedLevel: PropTypes.number
};

BasePredicateElement.propTypes = {
    ...commonPropTypes,
    dragHandleProps: PropTypes.object,
    previewOnly: PropTypes.bool
};
DraggablePredicateElement.propTypes = commonPropTypes;
PredicateElement.propTypes = {
    ...commonPropTypes,
    previewOnly: PropTypes.bool
};
